/**
 * Where a selection's bar stands.
 *
 * Where the bar stands is worked out afresh, from where what it acts on is on the screen now, every time
 * the plan moves — so it never trails the plan, never runs on after it, and comes back exactly as it was
 * when what it acts on does. It is centred just above what it acts on, or just below (on the side it came
 * up on):
 *
 *   - sideways, the screen's edge stops it while what it acts on is still under it — just clear of a control
 *     standing at that edge level with it (the inspector's pill), or of the open inspector; past that it goes
 *     with it;
 *   - towards the edge on its side (the top, for a bar above: the screen's own edge, or just under a control
 *     standing there) it goes round to the other side of what it acts on as soon as its own side would take
 *     it past that edge and the other would not, and back round to the side it came up on as soon as that
 *     has room again (with a little to spare: it never goes round and straight back). Going round is a
 *     glide riding on the plan — with it all the while, never over what it acts on once it is still;
 *   - towards the other edge it goes with what it acts on, under the tools with it;
 *   - where neither side has room (what it acts on all but as tall as the screen) it stops at the edge, over it.
 *
 * Else it moves on its own only when the plan is still and its place changes all the same (the inspector
 * opening beside it, a callout's box growing as it is typed): a glide too (`Settle`). Its selection's own
 * badge grown, it stays put under the finger.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export type Side = 'above' | 'below';
export interface View {
  scale: number;
  tx: number;
  ty: number;
}

/** What the bar is placed by, as the plan is now. */
export interface BarInput {
  /** The bar's size. */
  w: number;
  h: number;
  /** The screen within the edges the controls keep, and the controls (each grown by the room kept from it). */
  room: { l: number; t: number; r: number; b: number };
  controls: readonly Rect[];
  /** The middle of what it acts on, and the middle of what of it can be seen (where a bar coming up is centred). */
  mid: number;
  seen: number;
  /** What it acts on: its top and bottom where the bar stands. */
  top: number;
  bottom: number;
  /** The furthest right the bar's left may be (left of the open inspector). */
  maxX: number;
  /** The side it comes up on if it can. */
  prefer: Side;
  /** The other labels on the plan: a bar coming up keeps off one if it can. */
  labels: readonly Rect[];
  /** What it is the bar for, and the plan's view. */
  key: string;
  view: View;
}

/** The space kept between the bar and what it acts on. */
const GAP = 10;
/** How near the bar's end the middle of what it acts on may come. */
const KEEP = 24;
/** Over how much of a control's width the bar's edge comes down under it (sideways into it: smoothly, never a step). */
const RAMP = 96;
/** The room to spare on the side it came up on before it goes back round there. */
const SPARE = 8;

export class BarPlace {
  /** The side it came up on, the side it is on, how far aside it stands, and the point across it is fixed to (from `mid`, in the plan's own units). */
  #home: Side = 'above';
  #side: Side = 'above';
  #slide = 0;
  #fix = 0;
  /** Where it was last placed, for what, and with the plan seen how; null: it comes up afresh. */
  #last: { x: number; y: number; w: number; h: number; key: string; view: View } | null = null;
  readonly #settle = new Settle();

  /** Shown afresh (another selection, or the same one back after it moved): placed anew, no glide from anywhere. */
  reset(): void {
    this.#last = null;
    this.#settle.reset();
  }

  /** The point across what it acts on the bar is fixed to (where to read a mark's outline under it), the plan at `scale`. */
  anchor(mid: number, seen: number, scale: number): number {
    return this.#last ? mid + this.#fix * scale : seen;
  }

  /** Which side of what it acts on it is on now (a bar held at an edge: the side it keeps to). */
  side(i: BarInput): Side {
    const p = this.#at(i, this.#slide);
    return Math.abs(p.y - (i.bottom + GAP)) < 0.5 ? 'below' : Math.abs(p.y - (i.top - GAP - i.h)) < 0.5 ? 'above' : this.#side;
  }

  /** Where it stands, for the plan as it is (no glide). */
  get stands(): { x: number; y: number } | null {
    return this.#last && { x: this.#last.x, y: this.#last.y };
  }

  /** Placed for the plan as it is now (`now`: ms). */
  place(i: BarInput, now: number): void {
    const last = this.#last;
    const from = last ? this.#round(i) : (this.#comeUp(i), null);
    let p = this.#at(i, this.#slide);
    if (last && last.key === i.key && last.view.scale === i.view.scale && last.view.tx === i.view.tx && last.view.ty === i.view.ty) {
      // The plan still and its place changed all the same. Across, for its selection alone (its badge grown):
      // it stays put under the finger. Any other change (the inspector opened beside it, the bar grown, a
      // callout's box grown): a glide from where it was seen — its middle, and its edge by what it acts on.
      const was = { x: last.x + (last.w - i.w) / 2, y: last.y + (this.side(i) === 'above' ? last.h - i.h : 0) };
      if (last.w === i.w && Math.abs(was.x - p.x) > 0.5) {
        const slide = this.#slide + was.x - p.x;
        const q = this.#at(i, slide);
        if (Math.abs(q.x - was.x) < 0.5) {
          this.#slide = slide;
          p = q;
        }
      }
      this.#settle.kick(was.x - p.x, was.y - p.y, now);
    } else if (from) this.#settle.kick(from.x - p.x, from.y - p.y, now);
    this.#last = { ...p, w: i.w, h: i.h, key: i.key, view: { ...i.view } };
  }

  /** Where it is drawn at a time (ms): where it stands, and as far off it as a glide there still has to go. */
  drawn(now: number): { x: number; y: number; settling: boolean } {
    const o = this.#settle.at(now);
    return { x: (this.#last?.x ?? 0) + o.x, y: (this.#last?.y ?? 0) + o.y, settling: !o.still };
  }

  /**
   * Coming up: on the side it prefers where it can be seen there, else the other; and of its places, the one
   * covering least of the other labels (the side it prefers breaks ties) — a little aside past a label in its
   * way, never so far that it would seem to belong to something else. Covering a label for the moment the bar
   * is up beats a bar that has to be looked for.
   */
  #comeUp(i: BarInput): void {
    this.#fix = (i.seen - i.mid) / i.view.scale;
    const other: Side = i.prefer === 'above' ? 'below' : 'above';
    // (a quarter of its own width at most: it stays plainly centred on what it acts on)
    const reach = Math.min(110, Math.max(48, i.w * 0.25));
    const c = i.seen;
    const slides = [0, ...i.labels.flatMap((b) => [b.x - i.w - 8, b.x + b.w + 8]).map((x) => x - (c - i.w / 2))];
    let best = { side: i.prefer, slide: 0, cost: Infinity };
    for (const side of [i.prefer, other]) {
      this.#side = side;
      const home = this.#at(i, 0);
      // (truly on that side — not held at an edge over what it acts on — and wholly seen there)
      const there = Math.abs(home.y - (side === 'above' ? i.top - GAP - i.h : i.bottom + GAP)) < 0.5 && seen(i, home);
      for (const slide of slides) {
        const p = this.#at(i, slide);
        if (Math.abs(p.x - home.x) > reach || (slide && !seen(i, p))) continue;
        // (a step aside costs a little, the other side less still: either beats covering a label; a side it
        // cannot be seen on, almost anything)
        let cost = Math.abs(p.x - home.x) * 3 + (side === i.prefer ? 0 : 1) + (there ? 0 : 1e6);
        for (const b of i.labels) cost += overlap(p, i, b);
        if (cost < best.cost) best = { side, slide, cost };
      }
    }
    this.#home = this.#side = best.side;
    this.#slide = best.slide;
  }

  /** Round to the other side, or back to the side it came up on, for the plan as it is (see the top of this file): where it stood on the side it left, or null. */
  #round(i: BarInput): { x: number; y: number } | null {
    const other: Side = this.#home === 'above' ? 'below' : 'above';
    const to = this.#side === this.#home ? (!this.#fits(i, this.#home, 0) && this.#fits(i, other, 0) ? other : this.#home) : this.#fits(i, this.#home, SPARE) ? this.#home : other;
    if (to === this.#side) return null;
    const from = this.#at(i, this.#slide);
    this.#side = to;
    return from;
  }

  /** Whether a side keeps the bar short of the edge (or control) at that end of the screen, with room to spare. */
  #fits(i: BarInput, side: Side, spare: number): boolean {
    const { x } = this.#at(i, this.#slide, side);
    return side === 'above' ? i.top - GAP - i.h >= this.#edge(i, x, 'top') + spare : i.bottom + GAP <= this.#edge(i, x, 'bottom') - spare;
  }

  /** Where it stands for the plan as it is, `slide` aside, on a side (see the top of this file). */
  #at(i: BarInput, slide: number, side = this.#side): { x: number; y: number } {
    const c = i.mid + this.#fix * i.view.scale;
    const above = i.top - GAP - i.h,
      below = i.bottom + GAP;
    const level = side === 'above' ? above : below;
    let x = Math.max(this.#wall(i, level, 'left'), Math.min(this.#wall(i, level, 'right') - i.w, c - i.w / 2 + slide));
    x = Math.min(Math.max(c - i.w + KEEP, Math.min(c - KEEP, x)), i.maxX);
    if (side === 'above') {
      const lo = this.#edge(i, x, 'top');
      return { x, y: above >= lo ? above : Math.min(lo, below) };
    }
    const hi = this.#edge(i, x, 'bottom');
    return { x, y: below <= hi ? below : Math.max(hi, above) };
  }

  /**
   * How near the screen's left (or right) edge the bar may come with its top at `y`: the screen's own edge,
   * or just clear of a control standing at that edge part way down — reached smoothly over the bar's own
   * height as it comes up (or down) to it, so it is clear of it all the way past. (Controls in a corner are
   * the top's or the bottom's: see `#edge`.)
   */
  #wall(i: BarInput, y: number, end: 'left' | 'right'): number {
    let e = end === 'left' ? i.room.l : i.room.r;
    for (const c of i.controls) {
      const atEnd = end === 'left' ? c.x < i.room.l + 8 : c.x + c.w > i.room.r - 8;
      if (!atEnd || c.y < i.room.t + 8 || c.y + c.h > i.room.b - 8) continue;
      const ov = Math.min(y + i.h, c.y + c.h + i.h) - Math.max(y, c.y - i.h);
      if (ov <= 0) continue;
      const k = Math.min(1, ov / i.h);
      const f = k * k * (3 - 2 * k);
      e = end === 'left' ? Math.max(e, i.room.l + (c.x + c.w - i.room.l) * f) : Math.min(e, i.room.r - (i.room.r - c.x) * f);
    }
    return e;
  }

  /**
   * How near the screen's top (or bottom) the bar may come with its left at `x`: the screen's own edge, or
   * just clear of a control standing there — reached smoothly as the bar comes sideways under it.
   */
  #edge(i: BarInput, x: number, end: 'top' | 'bottom'): number {
    let e = end === 'top' ? i.room.t : i.room.b - i.h;
    for (const c of i.controls) {
      const atEnd = end === 'top' ? c.y < i.room.t + 8 : c.y + c.h > i.room.b - 8;
      if (!atEnd) continue;
      const ov = Math.min(x + i.w, c.x + c.w) - Math.max(x, c.x);
      if (ov <= 0) continue;
      const k = Math.min(1, ov / RAMP);
      const f = k * k * (3 - 2 * k);
      e = end === 'top' ? Math.max(e, i.room.t + (c.y + c.h - i.room.t) * f) : Math.min(e, i.room.b - i.h - (i.room.b - c.y) * f);
    }
    return e;
  }
}

/** Whether the bar can be wholly seen at a place: on the screen, and on no control. */
function seen(i: BarInput, p: { x: number; y: number }): boolean {
  if (p.x < i.room.l - 0.5 || p.x + i.w > i.room.r + 0.5 || p.y < i.room.t - 0.5 || p.y + i.h > i.room.b + 0.5) return false;
  return i.controls.every((c) => overlap(p, i, c) <= 0.5);
}

function overlap(p: { x: number; y: number }, size: { w: number; h: number }, b: Rect): number {
  const ow = Math.min(p.x + size.w, b.x + b.w) - Math.max(p.x, b.x);
  const oh = Math.min(p.y + size.h, b.y + b.h) - Math.max(p.y, b.y);
  return ow > 0 && oh > 0 ? ow * oh : 0;
}

/**
 * An offset that eases back to nothing as a critically damped spring does — no overshoot, no wobble.
 * Kicked by a move, the bar is drawn where it was and settles where it now belongs, keeping the pace it
 * had if a glide was under way.
 */
export class Settle {
  #x = 0;
  #y = 0;
  #vx = 0;
  #vy = 0;
  #t = 0;
  /** How quickly it settles (rad/s): most of the way in a sixth of a second, all of it in a third. */
  constructor(readonly omega = 22) {}
  /** The offset and its pace at a time (ms); `still` once nothing is left to see. */
  at(now: number): { x: number; y: number; vx: number; vy: number; still: boolean } {
    if (!this.#x && !this.#y && !this.#vx && !this.#vy) return { x: 0, y: 0, vx: 0, vy: 0, still: true };
    const w = this.omega;
    const t = Math.max(0, (now - this.#t) / 1000);
    const e = Math.exp(-w * t);
    const bx = this.#vx + w * this.#x,
      by = this.#vy + w * this.#y;
    const s = { x: (this.#x + bx * t) * e, y: (this.#y + by * t) * e, vx: (this.#vx - w * bx * t) * e, vy: (this.#vy - w * by * t) * e, still: false };
    if (Math.hypot(s.x, s.y) < 0.3 && Math.hypot(s.vx, s.vy) < 10) {
      this.reset();
      return { x: 0, y: 0, vx: 0, vy: 0, still: true };
    }
    return s;
  }
  /** The place moved by (dx, dy) at a time (ms): drawn where it was, settling from there. */
  kick(dx: number, dy: number, now: number): void {
    if (Math.hypot(dx, dy) < 0.5) return;
    const s = this.at(now);
    this.#x = s.x + dx;
    this.#y = s.y + dy;
    this.#vx = s.vx;
    this.#vy = s.vy;
    this.#t = now;
  }
  reset(): void {
    this.#x = this.#y = this.#vx = this.#vy = 0;
  }
}

export function visibleSpan(poly: readonly number[], W: number, H: number): { l: number; r: number } | null {
  let l = Infinity,
    r = -Infinity;
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const x0 = poly[2 * i]!,
      y0 = poly[2 * i + 1]!;
    const dx = poly[2 * j]! - x0,
      dy = poly[2 * j + 1]! - y0;
    // (Liang–Barsky: the part of the edge within the screen)
    let t0 = 0,
      t1 = 1;
    const ps = [-dx, dx, -dy, dy],
      qs = [x0, W - x0, y0, H - y0];
    let inside = true;
    for (let k = 0; k < 4 && inside; k++) {
      const p = ps[k]!,
        q = qs[k]!;
      if (p === 0) inside = q >= 0;
      else if (p < 0) t0 = Math.max(t0, q / p);
      else t1 = Math.min(t1, q / p);
      if (t0 > t1) inside = false;
    }
    if (!inside) continue;
    l = Math.min(l, x0 + t0 * dx, x0 + t1 * dx);
    r = Math.max(r, x0 + t0 * dx, x0 + t1 * dx);
  }
  for (const [cx, cy] of [[0, 0], [W, 0], [0, H], [W, H]] as const) {
    // (even–odd: a corner of the screen inside the outline)
    let c = false;
    for (let i = 0, k = n - 1; i < n; k = i++) {
      const xi = poly[2 * i]!,
        yi = poly[2 * i + 1]!,
        xk = poly[2 * k]!,
        yk = poly[2 * k + 1]!;
      if (yi > cy !== yk > cy && cx < ((xk - xi) * (cy - yi)) / (yk - yi) + xi) c = !c;
    }
    if (c) {
      l = Math.min(l, cx);
      r = Math.max(r, cx);
    }
  }
  return l === Infinity ? null : { l, r };
}
